/* Margin Finder · Telecom: o que o `agent_telecom` achou, um usuário por vez.
 *
 * A leitura vai de decisão → evidência → ação → método → ressalvas → procedência.
 *
 * O herói tem três estados porque o `status` do agente tem três: `trocar` (há combo
 * mais barato), `manter` (o melhor combo elegível custa MAIS que o de hoje, e o
 * agente zera a economia em vez de publicar número negativo) e `dados_insuficientes`
 * (não há base comparável — ali não sai número nenhum, porque um número inventado
 * seria mentira).
 */

import { ChartFrame, Card } from "../../components/ui";
import type { TelecomPlano, TelecomUser } from "../../lib/api";
import { brl2, intBR } from "../../lib/format";
import { RelatorioEmTexto } from "./Relatorio";
import {
  Barras, BlocoAuditoria, CONFIANCA, Facts, Hero, Passos, PlanoShell, Provenancia,
  SecaoMargin, economiaLabel, lista, money, ou, rotulo,
} from "./comum";

/* ── rótulos ─────────────────────────────────────────────────────────────── */

const PAPEL: Record<string, string> = {
  mesma_operadora: "na mesma operadora",
  outra_grande: "outra grande",
  alternativa: "alternativa",
};

const MODALIDADE: Record<string, string> = {
  "pos-pago": "pós-pago",
  familia: "família",
  controle: "controle",
};

const STATUS: Record<string, string> = {
  trocar: "vale trocar",
  manter: "vale manter",
  dados_insuficientes: "dados insuficientes",
};

/* ── o cartão de combo ───────────────────────────────────────────────────── */

function ComboCard({ plano, escolhido }: { plano: TelecomPlano; escolhido: boolean }) {
  const medioDifere = plano.custo_medio_12m_brl !== null
    && plano.preco_mensal_brl !== null
    && Math.abs(plano.custo_medio_12m_brl - plano.preco_mensal_brl) >= 0.01;

  return (
    <PlanoShell
      papel={rotulo(PAPEL, plano.papel)}
      escolhido={escolhido}
      titulo={plano.plano}
      sub={plano.operadora}
      preco={money(plano.preco_mensal_brl)}
      precoNota="/mês"
      porQue={plano.por_que}
      atencao={plano.atencao}
      fonteUrl={plano.fonte_url}
      chips={
        <>
          <span className="chip">{rotulo(MODALIDADE, plano.modalidade)}</span>
          {plano.bonus_expira && <span className="tag alta">bônus expira</span>}
          {plano.confianca_do_encaixe === "baixa" && (
            <span className="tag alta">{rotulo(CONFIANCA, plano.confianca_do_encaixe)}</span>
          )}
        </>
      }
    >
      {medioDifere && (
        <div className="mf-plan-linha">
          média de 12 meses: {money(plano.custo_medio_12m_brl)}/mês
        </div>
      )}
      {/* O penhasco da promoção: o número que a vitrine não mostra. */}
      {plano.preco_pos_promo_brl !== null && (
        <div className="mf-plan-linha">
          sobe para {money(plano.preco_pos_promo_brl)}/mês depois da promoção
        </div>
      )}
      <div className="mf-plan-franquia">
        {plano.internet_mbps !== null && <>{intBR(plano.internet_mbps)} Mbps</>}
        {plano.movel_gb_base !== null && <> · {intBR(plano.movel_gb_base)} GB móvel</>}
        {plano.movel_gb_anunciado !== null
          && plano.movel_gb_anunciado !== plano.movel_gb_base
          && <> (anunciado {intBR(plano.movel_gb_anunciado)} GB)</>}
      </div>
      <div className="mf-plan-cobertura">
        {plano.linhas_inclusas === null
          ? "linhas incluídas não declaradas"
          : `${intBR(plano.linhas_inclusas)} linha(s) inclusa(s)`}
      </div>
    </PlanoShell>
  );
}

/* ── a sessão ────────────────────────────────────────────────────────────── */

export function Telecom() {
  return (
    <SecaoMargin<TelecomUser>
      chave="telecom"
      vazio="Usuário sem análise de telecom."
      optionLabel={economiaLabel}
    >
      {(user) => <TelecomDetalhe user={user} />}
    </SecaoMargin>
  );
}

function TelecomDetalhe({ user }: { user: TelecomUser }) {
  const a = user.analise;
  const propostas = lista(a.propostas);
  const auditoria = a.auditoria ?? {} as typeof a.auditoria;
  const neg = a.negociacao;

  const barras = [
    ...(user.custo_atual_brl !== null
      ? [{ key: "hoje", rotulo: "hoje", valor: user.custo_atual_brl, destaque: true }]
      : []),
    ...propostas
      .filter((p) => p.custo_medio_12m_brl !== null)
      .map((p) => ({
        key: `p${p.id_catalogo}`,
        rotulo: p.operadora,
        valor: p.custo_medio_12m_brl as number,
        qtd: p.id_catalogo === a.melhor_plano_id_catalogo ? "escolhido" : "",
      })),
  ];

  return (
    <div className="mf-page">
      {/* 1 · a decisão */}
      <Card>
        <Hero
          estado={user.estado_economia}
          titulo="economia estimada"
          valor={user.economia_anual_brl === null
            ? "sem base comparável"
            : `R$ ${brl2(user.economia_anual_brl)}`}
          detalhe={user.economia_anual_brl === null
            ? "O agente não achou lançamento suficiente para comparar."
            : <>por ano · {money(user.economia_mensal_brl)}/mês</>}
          lado={user.custo_novo_brl === null ? undefined : {
            k: "combo indicado",
            v: `${money(user.custo_novo_brl)}/mês`,
            d: <>{ou(user.plano_escolhido_operadora)} · {ou(user.plano_escolhido_nome)}</>,
          }}
        />
        <p className="mf-frase">
          {a.status !== "trocar" && (
            <span className="tag" style={{ marginRight: 8 }}>{rotulo(STATUS, a.status)}</span>
          )}
          {a.resumo}
        </p>
      </Card>

      {/* 2 · a evidência: o que sai hoje */}
      <Card title="Situação atual" sub="o que o agente encontrou nos lançamentos">
        {a.base_comparavel === false && (
          <div className="banner" role="status">
            <span aria-hidden>⚠</span>
            <span>
              Sem base comparável: os lançamentos não bastam para dizer quanto sai por
              mês em telecom, então não há economia a apurar.
            </span>
          </div>
        )}
        <Facts items={[
          { k: "operadora de hoje", v: ou(a.operadora_atual) },
          { k: "sai por mês", v: money(a.valor_atual_brl) },
          { k: "UF derivada", v: ou(auditoria.uf_derivada) },
          { k: "veredito", v: rotulo(STATUS, a.status), tag: "tag" },
        ]} />
      </Card>

      {/* 3 · a ação: para onde ir */}
      {propostas.length > 0 && (
        <ChartFrame
          title="Para onde ir"
          sub="combos elegíveis do catálogo, o escolhido em destaque"
          tableHeader={["papel", "operadora", "plano", "R$/mês", "médio 12m", "Mbps", "GB", "linhas"]}
          tableRows={propostas.map((p) => [
            rotulo(PAPEL, p.papel), p.operadora, p.plano,
            p.preco_mensal_brl ?? "—", p.custo_medio_12m_brl ?? "—",
            p.internet_mbps ?? "—", p.movel_gb_base ?? "—", p.linhas_inclusas ?? "—",
          ])}
        >
          <div className="mf-plans">
            {propostas.map((p) => (
              <ComboCard key={p.id_catalogo} plano={p}
                         escolhido={p.id_catalogo === a.melhor_plano_id_catalogo} />
            ))}
          </div>
        </ChartFrame>
      )}

      {/* 4 · a ação: como negociar onde já se está */}
      {neg && (
        <Card title="Como negociar" sub="o caminho e as alavancas na operadora de hoje">
          <Facts items={[
            { k: "operadora", v: ou(neg.operadora) },
            { k: "quando ligar", v: ou(neg.quando) },
          ]} />
          <p className="mf-nota">{neg.canal}</p>
          {lista(neg.alavancas).length > 0 && (
            <ol className="mf-checklist">
              {lista(neg.alavancas).map((x, i) => <li key={i}>{x}</li>)}
            </ol>
          )}
          {neg.se_travarem && <p className="mf-atencao">{neg.se_travarem}</p>}
          {neg.fonte_url && (
            <a className="mf-source" href={neg.fonte_url} target="_blank" rel="noreferrer">
              fonte: {neg.fonte_url}
            </a>
          )}
        </Card>
      )}

      {/* 5 · a ação: a lista antes de assinar */}
      <Passos title="Antes de contratar" items={lista(a.antes_de_contratar)} />

      {/* 6 · o método */}
      {barras.length > 1 && (
        <ChartFrame
          title="O custo de cada saída"
          sub="média de 12 meses por combo, contra o que sai hoje"
          tableHeader={["opção", "R$/mês"]}
          tableRows={barras.map((b) => [b.rotulo, b.valor])}
        >
          <Barras rows={barras} />
        </ChartFrame>
      )}

      {/* 7 · as ressalvas */}
      <Card title="Premissas e ressalvas" sub="o que o agente assumiu para chegar aqui">
        {lista(a.premissas).length > 0 && (
          <ul className="mf-why">
            {lista(a.premissas).map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        )}
        <BlocoAuditoria
          limitacoes={lista(auditoria.limitacoes)}
          blocosRecebidos={lista(auditoria.blocos_recebidos)}
          blocosAusentes={lista(auditoria.blocos_ausentes)}
          idsCitados={lista(auditoria.ids_citados)}
          regrasVioladas={lista(auditoria.regras_de_piso_violadas)}
        >
          <Facts items={[
            { k: "combos no catálogo", v: auditoria.combos_no_catalogo?.toString() ?? "—" },
            { k: "combos elegíveis", v: auditoria.combos_elegiveis?.toString() ?? "—" },
          ]} />
          {lista(auditoria.filtros_aplicados).length > 0 && (
            <ul className="mf-why">
              {lista(auditoria.filtros_aplicados).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          )}
          {lista(auditoria.crivos_relaxados).length > 0 && (
            <p className="mf-nota">
              Crivos relaxados: {lista(auditoria.crivos_relaxados).join(", ")}
            </p>
          )}
        </BlocoAuditoria>
      </Card>

      {/* 8 · o texto do agente */}
      <RelatorioEmTexto userId={user.user_id} agentId="agent_telecom" />

      {/* 9 · a procedência */}
      <Provenancia user={user} agentId="agent_telecom" />
    </div>
  );
}
